import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';
import type {
  HisActivitySummary,
  PaginatedResponse,
  Settlement,
  SettlementEvent,
  SettlementFileSummary,
  SettlementLine,
} from '@core/types/api';

/** Which decision a batch is taking. */
export type LineResolution = 'reconciled' | 'claimed';

/** What a batch changed: the rest were already decided by somebody else. */
export interface BulkResolution {
  resolved: number;
  skipped: number;
}

/** A page of statements as the list consumes it. */
export interface SettlementPage {
  results: Settlement[];
  count: number;
}

/** What the settlements screen filters by: the insurers that settled here, and the counts. */
export interface SettlementFacets {
  insurers: { slug: string; name: string }[];
  statuses: Record<string, number>;
}

export interface ListSettlementsArgs {
  page: number;
  pageSize: number;
  ordering?: string;
  search?: string;
  /** Any of these insurers, by slug (repeated in the query string). */
  insurers?: string[];
  /** Any of these states (repeated in the query string). */
  statuses?: string[];
  /** Status codes whose label matched the search term (the SPA owns the labels). */
  statusAny?: string[];
  /** The day the insurer issued the statement, both ends inclusive (ISO). */
  issuedFrom?: string;
  issuedTo?: string;
}

/**
 * What the settlements list asks the server for.
 *
 * Same contract as `lineParams`: one place where a filter the screen offers turns
 * into the parameter the server reads.
 */
export function settlementParams({
  page,
  pageSize,
  ordering,
  search,
  insurers,
  statuses,
  statusAny,
  issuedFrom,
  issuedTo,
}: ListSettlementsArgs): URLSearchParams {
  // A query string rather than a params object: a filter with several values is
  // the same key repeated, which `URLSearchParams` can say and a plain object cannot.
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (ordering) params.set('ordering', ordering);
  if (search) params.set('search', search);
  for (const insurer of insurers ?? []) params.append('insurer', insurer);
  for (const status of statuses ?? []) params.append('status', status);
  if (statusAny?.length) params.set('status_any', statusAny.join(','));
  if (issuedFrom) params.set('issued_from', issuedFrom);
  if (issuedTo) params.set('issued_to', issuedTo);
  return params;
}

/** A page of lines as the reconciliation table consumes it. */
export interface LinePage {
  results: SettlementLine[];
  count: number;
}

export interface ListLinesArgs {
  settlement: string;
  page: number;
  pageSize: number;
  ordering?: string;
  search?: string;
  /** Status codes whose label matched the search term (the SPA owns the labels). */
  statusAny?: string[];
  /** Resolution codes whose label matched the search term. */
  resolutionAny?: string[];
  /** Only differences nobody has decided yet. */
  mismatched?: boolean;
  /** Only lines somebody already reconciled or claimed. */
  decided?: boolean;
  /** One reason, when a tray asks for it. */
  status?: string;
  /** The specialty the insurer names on the line. */
  specialty?: string;
  /** The day the act was delivered, both ends inclusive (ISO). */
  serviceFrom?: string;
  serviceTo?: string;
}

/**
 * What the table asks the server for, as query parameters.
 *
 * Exported because it is the one place a new filter can be added to the table's
 * state and forgotten here, which looks exactly like the server ignoring it.
 */
export function lineParams({
  settlement,
  page,
  pageSize,
  ordering,
  search,
  statusAny,
  resolutionAny,
  mismatched,
  decided,
  status,
  specialty,
  serviceFrom,
  serviceTo,
}: ListLinesArgs): Record<string, string | number> {
  return {
    settlement,
    page,
    page_size: pageSize,
    ...(ordering ? { ordering } : {}),
    ...(search ? { search } : {}),
    ...(statusAny?.length ? { status_any: statusAny.join(',') } : {}),
    ...(resolutionAny?.length ? { resolution_any: resolutionAny.join(',') } : {}),
    ...(mismatched ? { mismatched: 'true' } : {}),
    ...(decided ? { decided: 'true' } : {}),
    ...(status ? { status } : {}),
    ...(specialty ? { specialty } : {}),
    ...(serviceFrom ? { service_from: serviceFrom } : {}),
    ...(serviceTo ? { service_to: serviceTo } : {}),
  };
}

function unwrapList<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  return (response as PaginatedResponse<T> | null)?.results ?? [];
}

/**
 * Reconciliation API.
 *
 * ORGANIZATION-scoped: every row belongs to one centre, so its cache is dropped
 * when the active organization changes (see `TENANT_SCOPED_APIS` in the store).
 *
 * `downloadFile` is a mutation on purpose: a query would keep the Blob in the
 * cache forever. It hands back an object URL, which the caller clicks and revokes.
 */
export const reconciliationApi = createApi({
  reducerPath: 'reconciliationApi',
  baseQuery,
  tagTypes: ['Settlement', 'Line', 'Event'],
  endpoints: (builder) => ({
    listSettlements: builder.query<SettlementPage, ListSettlementsArgs>({
      query: (args) => ({ url: `/api/reconciliation/settlements/?${settlementParams(args)}`, method: 'GET' }),
      transformResponse: (response: PaginatedResponse<Settlement>) => ({
        results: response?.results ?? [],
        count: response?.count ?? 0,
      }),
      providesTags: ['Settlement'],
    }),

    getSettlementFacets: builder.query<SettlementFacets, void>({
      query: () => ({ url: '/api/reconciliation/settlements/facets/', method: 'GET' }),
      providesTags: ['Settlement'],
    }),

    getSettlement: builder.query<Settlement, string>({
      query: (uuid) => ({ url: `/api/reconciliation/settlements/${uuid}/`, method: 'GET' }),
      providesTags: ['Settlement'],
    }),

    listLines: builder.query<LinePage, ListLinesArgs>({
      query: (args) => ({ url: '/api/reconciliation/lines/', method: 'GET', params: lineParams(args) }),
      transformResponse: (response: PaginatedResponse<SettlementLine>) => ({
        results: response?.results ?? [],
        count: response?.count ?? 0,
      }),
      providesTags: ['Line'],
    }),

    listEvents: builder.query<SettlementEvent[], string>({
      query: (settlement) => ({
        url: '/api/reconciliation/events/',
        method: 'GET',
        params: { settlement__uuid: settlement, page_size: 100 },
      }),
      transformResponse: (response: unknown) => unwrapList<SettlementEvent>(response),
      providesTags: ['Event'],
    }),

    listFiles: builder.query<SettlementFileSummary[], string>({
      query: (settlement) => ({
        url: '/api/reconciliation/files/',
        method: 'GET',
        params: { settlements__uuid: settlement },
      }),
      transformResponse: (response: unknown) => unwrapList<SettlementFileSummary>(response),
      providesTags: ['Settlement'],
    }),

    downloadFile: builder.mutation<string, string>({
      query: (uuid) => ({
        url: `/api/reconciliation/files/${uuid}/download/`,
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
      transformResponse: (blob: Blob) => URL.createObjectURL(blob),
    }),

    /** The centre's own activity nothing is paired with, for the picker. */
    searchActivities: builder.query<HisActivitySummary[], { search?: string }>({
      query: ({ search }) => ({
        url: '/api/reconciliation/activities/',
        method: 'GET',
        params: { ...(search ? { search } : {}) },
      }),
      transformResponse: (response: unknown) => unwrapList<HisActivitySummary>(response),
      providesTags: ['Line'],
    }),

    /** Pair a line with the act the centre recorded for it. */
    matchLine: builder.mutation<SettlementLine, { uuid: string; hisActivity: string; note: string }>({
      query: ({ uuid, hisActivity, note }) => ({
        url: `/api/reconciliation/lines/${uuid}/match/`,
        method: 'POST',
        body: { his_activity: hisActivity, note },
      }),
      invalidatesTags: ['Settlement', 'Line', 'Event'],
    }),

    /** Undo a pairing somebody made by hand. */
    unmatchLine: builder.mutation<SettlementLine, string>({
      query: (uuid) => ({ url: `/api/reconciliation/lines/${uuid}/unmatch/`, method: 'POST' }),
      invalidatesTags: ['Settlement', 'Line', 'Event'],
    }),

    /** One decision over the lines the reader ticked; see `bulk_resolve` in the API. */
    resolveLines: builder.mutation<BulkResolution, { uuids: string[]; resolution: LineResolution; note: string }>({
      query: (body) => ({
        url: '/api/reconciliation/lines/resolve/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Settlement', 'Line', 'Event'],
    }),

    reconcileLine: builder.mutation<SettlementLine, { uuid: string; note: string }>({
      query: ({ uuid, note }) => ({
        url: `/api/reconciliation/lines/${uuid}/reconcile/`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: ['Settlement', 'Line', 'Event'],
    }),

    claimLine: builder.mutation<SettlementLine, { uuid: string; note: string }>({
      query: ({ uuid, note }) => ({
        url: `/api/reconciliation/lines/${uuid}/claim/`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: ['Settlement', 'Line', 'Event'],
    }),
  }),
});

export const {
  useListSettlementsQuery,
  useGetSettlementFacetsQuery,
  useGetSettlementQuery,
  useListLinesQuery,
  useListEventsQuery,
  useListFilesQuery,
  useDownloadFileMutation,
  useReconcileLineMutation,
  useClaimLineMutation,
  useResolveLinesMutation,
  useSearchActivitiesQuery,
  useMatchLineMutation,
  useUnmatchLineMutation,
} = reconciliationApi;
